import { base64ToBytes, bytesToBase64 } from '../utils/base64';

export type PixelPredicate = (x: number, y: number) => boolean;

export class MaskLayer {
  readonly width: number;
  readonly height: number;
  readonly initialCount: number;
  private readonly initial: Uint8Array;
  private active: Uint8Array;
  private remainingCountValue: number;

  constructor(width: number, height: number, isTargetPixel: PixelPredicate) {
    if (width <= 0 || height <= 0) {
      throw new RangeError('遮罩尺寸必须大于 0');
    }

    this.width = width;
    this.height = height;
    this.initial = new Uint8Array(width * height);
    let count = 0;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const enabled = isTargetPixel(x, y) ? 1 : 0;
        this.initial[this.indexOf(x, y)] = enabled;
        count += enabled;
      }
    }

    this.active = this.initial.slice();
    this.initialCount = count;
    this.remainingCountValue = count;
  }

  get remainingCount(): number {
    return this.remainingCountValue;
  }

  get clearedRatio(): number {
    return this.initialCount === 0 ? 1 : 1 - this.remainingCountValue / this.initialCount;
  }

  isActive(x: number, y: number): boolean {
    return this.isInside(x, y) && this.active[this.indexOf(x, y)] === 1;
  }

  clearCircle(centerX: number, centerY: number, radius: number): number {
    if (radius < 0) {
      throw new RangeError('笔刷半径不能小于 0');
    }

    const minX = Math.max(0, centerX - radius);
    const maxX = Math.min(this.width - 1, centerX + radius);
    const minY = Math.max(0, centerY - radius);
    const maxY = Math.min(this.height - 1, centerY + radius);
    const radiusSquared = radius * radius;
    let changed = 0;

    for (let y = minY; y <= maxY; y += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        const dx = x - centerX;
        const dy = y - centerY;
        if (dx * dx + dy * dy > radiusSquared) {
          continue;
        }

        const index = this.indexOf(x, y);
        if (this.active[index] !== 1) {
          continue;
        }

        this.active[index] = 0;
        this.remainingCountValue -= 1;
        changed += 1;
      }
    }

    return changed;
  }

  reset(): void {
    this.active = this.initial.slice();
    this.remainingCountValue = this.initialCount;
  }

  serialize(): string {
    return bytesToBase64(this.active);
  }

  restore(encoded: string): void {
    const restored = base64ToBytes(encoded, this.active.length);
    let remaining = 0;
    for (let index = 0; index < restored.length; index += 1) {
      const value = restored[index] === 1 && this.initial[index] === 1 ? 1 : 0;
      restored[index] = value;
      remaining += value;
    }
    this.active = restored;
    this.remainingCountValue = remaining;
  }

  private isInside(x: number, y: number): boolean {
    return x >= 0 && x < this.width && y >= 0 && y < this.height;
  }

  private indexOf(x: number, y: number): number {
    return y * this.width + x;
  }
}
